"use client"

import { useEffect, useState } from "react"
import { X, Search, Users, Heart } from "lucide-react"
import { fetchUserFollowers, fetchUserFollowing } from "@/lib/community/community-api"
import { toggleFollow } from "@/lib/community/service/interaction-service"
import { useUserStore } from "@/lib/community/store/user-store"
import { useAuthContext } from "@/lib/auth-context"
import { useToast } from "@/lib/hooks/use-toast"
import { ApiError } from "@/lib/api"
import type { CommunityUser } from "@/lib/community/types"
import { CreatorLevelBadge } from "@/src/components/community/creator-level-badge"
import { CommunityAvatar } from "@/src/components/community/community-avatar"

function UserCard({
  user,
  isFollowed,
  isBusy,
  isHovered,
  isSelf,
  onFollow,
  onHoverEnter,
  onHoverLeave,
}: {
  user: CommunityUser
  isFollowed: boolean
  isBusy: boolean
  isHovered: boolean
  isSelf: boolean
  onFollow: () => void
  onHoverEnter: () => void
  onHoverLeave: () => void
}) {
  const handleNavigate = () => {
    if (isSelf) return
    window.open(`${import.meta.env.BASE_URL}community/user/${user.id}`, "_blank")
  }

  return (
    <div className="group flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-accent/40">
      {/* Avatar + Info — clickable to open profile */}
      <button
        type="button"
        onClick={handleNavigate}
        className={`flex items-center gap-3 flex-1 min-w-0 text-left ${isSelf ? "" : "cursor-pointer"}`}
      >
        <CommunityAvatar
          src={user.avatar}
          name={user.displayName}
          sizeClass="w-10 h-10"
          className="ring-2 ring-border/20 transition-shadow group-hover:ring-primary/30 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium text-sm truncate">{user.displayName}</span>
            <CreatorLevelBadge level={user.creatorLevel} />
          </div>
          {user.bio ? (
            <p className="text-xs text-muted-foreground truncate">{user.bio}</p>
          ) : (
            <p className="text-xs text-muted-foreground/50">{user.stats.followers} 粉丝 · {user.stats.posts} 作品</p>
          )}
        </div>
      </button>

      {/* Follow button */}
      <button
        type="button"
        disabled={isBusy}
        onClick={onFollow}
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
        className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
          isFollowed
            ? isHovered
              ? "bg-destructive/10 text-destructive border border-destructive/30"
              : "bg-secondary text-muted-foreground border border-border"
            : "bg-primary text-primary-foreground shadow-sm hover:opacity-90"
        } ${isBusy ? "opacity-50 pointer-events-none" : ""}`}
      >
        {isFollowed
          ? isHovered ? "取消关注" : "已关注"
          : "关注"}
      </button>
    </div>
  )
}

export function FollowerListModal({
  userId,
  displayName,
  initialTab,
  onClose,
}: {
  userId: string
  displayName: string
  initialTab: "followers" | "following"
  onClose: () => void
}) {
  const [tab, setTab] = useState<"followers" | "following">(initialTab)
  const [userIds, setUserIds] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)
  const [followBusy, setFollowBusy] = useState<Record<string, boolean>>({})
  const [hoverUserId, setHoverUserId] = useState<string | null>(null)
  const { isLoggedIn, openLogin, currentUser } = useAuthContext()
  const toast = useToast()

  // Subscribe to user store so follow state updates reactively
  const storeEntities = useUserStore((s) => s.entities)

  const handleFollow = async (targetId: string) => {
    if (!isLoggedIn) { openLogin(); return }
    if (followBusy[targetId]) return
    setFollowBusy((s) => ({ ...s, [targetId]: true }))
    try {
      const nowFollowed = await toggleFollow(targetId)
      toast.success(nowFollowed ? "关注成功" : "已取消关注")
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return
      toast.error(err instanceof ApiError ? err.statusText : "操作失败，请重试")
    } finally {
      setFollowBusy((s) => ({ ...s, [targetId]: false }))
    }
  }

  useEffect(() => {
    setUserIds([])
    setLoading(true)
    const fn = tab === "followers" ? fetchUserFollowers : fetchUserFollowing
    fn(userId, 1).then((data) => {
      // Upsert into user store so toggleFollow can find them
      useUserStore.getState().upsertUsers(data.users)
      setUserIds(data.users.map((u) => u.id))
      setLoading(false)
    })
  }, [tab, userId])

  const users = userIds
    .map((id) => storeEntities[id])
    .filter((u): u is CommunityUser => !!u)

  const filteredUsers = users.filter((u) =>
    u.displayName.toLowerCase().includes(search.toLowerCase())
  )

  const tabs = [
    { key: "followers" as const, label: "粉丝", icon: Heart },
    { key: "following" as const, label: "关注", icon: Users },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-background/95 backdrop-blur-2xl border border-border/60 rounded-2xl shadow-2xl max-w-md w-full max-h-[70vh] flex flex-col m-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <h2 className="text-base font-semibold">{displayName}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">社交关系</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mx-5 mb-3 p-1 bg-muted/40 rounded-xl">
          {tabs.map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  tab === t.key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            )
          })}
        </div>

        {/* Search */}
        <div className="relative mx-5 mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
          <input
            type="text"
            placeholder="搜索用户..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-muted/30 border border-border/40 rounded-xl text-sm placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all"
          />
        </div>

        {/* Divider */}
        <div className="mx-5 h-px bg-border/40" />

        {/* List */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {filteredUsers.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 rounded-full bg-muted/40 flex items-center justify-center mb-3">
                {tab === "followers" ? (
                  <Heart className="w-5 h-5 text-muted-foreground/40" />
                ) : (
                  <Users className="w-5 h-5 text-muted-foreground/40" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {tab === "followers" ? "还没有粉丝" : "还没有关注任何人"}
              </p>
              <p className="text-xs text-muted-foreground/50 mt-1">
                {tab === "followers" ? "发布优质内容来吸引粉丝吧" : "去社区看看有趣的创作者"}
              </p>
            </div>
          ) : (
            filteredUsers.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                isFollowed={u.isFollowed ?? false}
                isBusy={followBusy[u.id] ?? false}
                isHovered={hoverUserId === u.id}
                isSelf={currentUser?.userId === u.id}
                onFollow={() => handleFollow(u.id)}
                onHoverEnter={() => setHoverUserId(u.id)}
                onHoverLeave={() => setHoverUserId(null)}
              />
            ))
          )}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 border-2 border-muted-foreground/20 border-t-primary rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
